/**
 * Product Slider Horizontal Component
 * Handles slider functionality with responsive behavior
 */

class ProductSliderHorizontal {
  constructor(element) {
    this.slider = element;
    this.container = this.slider.querySelector('.hdt-slider__container');
    this.viewport = this.slider.querySelector('.hdt-slider__viewport');
    this.cards = Array.from(this.slider.querySelectorAll('.hdt-product-card-horizontal'));
    
    const wrapper = this.slider.closest('.hdt-slider-wrapper');
    this.prevBtn = wrapper ? wrapper.querySelector('.hdt-slider-nav--prev') : null;
    this.nextBtn = wrapper ? wrapper.querySelector('.hdt-slider-nav--next') : null;
    this.dotsContainer = wrapper ? wrapper.querySelector('.hdt-slider-dots') : null;
    
    // Settings
    this.autoplay = this.slider.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.slider.dataset.autoplaySpeed) || 3000;
    this.sliderSpeed = parseInt(this.slider.dataset.sliderSpeed) || 500;
    this.itemsDesktop = parseFloat(this.slider.dataset.itemsDesktop) || 6;
    this.itemsMobile = parseFloat(this.slider.dataset.itemsMobile) || 1.5;
    
    // State
    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    
    this.init();
  }

  init() {
    if (!this.container || this.cards.length === 0) {
      console.error('❌ No container or cards found');
      return;
    }

    this.updateSliderSettings();
    this.createDots();
    this.setupNavigation();
    this.setupTouchEvents();
    
    if (this.autoplay && window.innerWidth > 768) {
      this.startAutoplay();
    }

    window.addEventListener('resize', this.handleResize.bind(this));
  }

  getGap() {
    const screenWidth = window.innerWidth;
    return screenWidth <= 768 ? 12 : 20;
  }

  updateSliderSettings() {
    const screenWidth = window.innerWidth;
    
    // Determine items per view based on screen size
    if (screenWidth <= 768) {
      // Mobile
      this.itemsPerView = this.itemsMobile;
    } else if (screenWidth <= 1024) {
      // Tablet
      this.itemsPerView = 3;
    } else if (screenWidth <= 1280) {
      // Large Tablet / Small Desktop
      this.itemsPerView = 4;
    } else {
      // Desktop
      this.itemsPerView = this.itemsDesktop;
    }
    
    // Calculate total pages correctly for fractional items per view
    this.totalPages = Math.ceil(this.cards.length / this.itemsPerView);
    this.maxIndex = Math.max(0, this.totalPages - 1);
    
    // Show/hide navigation based on whether multiple pages exist
    this.updateNavigationVisibility();
    
    // Reset position if current index is out of bounds
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
      this.updateSliderPosition(false);
    }
    
    this.updateNavButtons();
  }

  updateNavigationVisibility() {
    const needsNavigation = this.totalPages > 1;
    const wrapper = this.slider.closest('.hdt-slider-wrapper');
    
    if (!wrapper) return;
    
    const navigation = wrapper.querySelector('.hdt-slider-navigation');
    const dotsContainer = wrapper.querySelector('.hdt-slider-dots');
    // Show/hide navigation arrows
    if (navigation) {
      navigation.style.display = needsNavigation ? 'flex' : 'none';
    }
    // Show/hide dots
    if (dotsContainer) {
      dotsContainer.style.display = needsNavigation ? 'flex' : 'none';
    }
  }

  createDots() {
    if (!this.dotsContainer) {
      console.warn('⚠️ No dots container found');
      return;
    }
    
    this.dotsContainer.innerHTML = '';
    const totalDots = this.totalPages || Math.ceil(this.cards.length / this.itemsPerView);

    
    // Only create dots if there are multiple pages
    if (totalDots <= 1) {
      return;
    }
    
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'hdt-slider-dot';
      dot.setAttribute('aria-label', `Ir a la página ${i + 1}`);
      dot.setAttribute('type', 'button');
      if (i === 0) dot.classList.add('active');
      
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.currentIndex = i;
        this.updateSliderPosition(true);
      });
      
      this.dotsContainer.appendChild(dot);
    }
    
  }

  setupNavigation() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.prev();
      });
    } else {
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.next();
      });
    } else {
    }
  }

  setupTouchEvents() {
    // Only for desktop - mobile uses native scroll
    if (window.innerWidth <= 768) return;

    this.container.addEventListener('mousedown', this.touchStart.bind(this));
    this.container.addEventListener('touchstart', this.touchStart.bind(this));
    
    this.container.addEventListener('mousemove', this.touchMove.bind(this));
    this.container.addEventListener('touchmove', this.touchMove.bind(this));
    
    this.container.addEventListener('mouseup', this.touchEnd.bind(this));
    this.container.addEventListener('mouseleave', this.touchEnd.bind(this));
    this.container.addEventListener('touchend', this.touchEnd.bind(this));
    
    // Prevent context menu
    this.container.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  touchStart(e) {
    if (window.innerWidth <= 768) return;
    
    this.isDragging = true;
    this.startPos = this.getPositionX(e);
    this.container.style.cursor = 'grabbing';
    
    if (this.autoplay) {
      this.stopAutoplay();
    }
  }

  touchMove(e) {
    if (!this.isDragging || window.innerWidth <= 768) return;
    
    const currentPosition = this.getPositionX(e);
    const diff = currentPosition - this.startPos;
    
    // Add resistance at boundaries
    const cardWidth = this.cards[0].offsetWidth;
    const gap = this.getGap();
    const maxTranslate = 0;
    const maxItemsToMove = this.maxIndex * this.itemsPerView;
    const minTranslate = -(maxItemsToMove * (cardWidth + gap));
    
    let newTranslate = this.prevTranslate + diff;
    
    // Apply resistance
    if (newTranslate > maxTranslate) {
      newTranslate = maxTranslate + (newTranslate - maxTranslate) * 0.3;
    } else if (newTranslate < minTranslate) {
      newTranslate = minTranslate + (newTranslate - minTranslate) * 0.3;
    }
    
    this.currentTranslate = newTranslate;
    this.container.style.transition = 'none';
    this.container.style.transform = `translateX(${this.currentTranslate}px)`;
  }

  touchEnd() {
    if (!this.isDragging || window.innerWidth <= 768) return;
    
    this.isDragging = false;
    this.container.style.cursor = 'grab';
    
    const movedBy = this.currentTranslate - this.prevTranslate;
    const cardWidth = this.cards[0].offsetWidth;
    const pageWidth = this.itemsPerView * cardWidth;
    
    // Determine if we should move to next/prev page based on movement
    if (movedBy < -(pageWidth / 3) && this.currentIndex < this.maxIndex) {
      this.currentIndex++;
    } else if (movedBy > (pageWidth / 3) && this.currentIndex > 0) {
      this.currentIndex--;
    }
    
    this.updateSliderPosition();
    
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  getPositionX(e) {
    return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateSliderPosition();
      
      if (this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }
  }

  next() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateSliderPosition();
      
      if (this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }
  }

  updateSliderPosition(animated = true) {
    const isMobile = window.innerWidth <= 768;
    
    
    if (isMobile) {
      // On mobile, use native scroll
      const cardWidth = this.cards[0].offsetWidth;
      const gap = this.getGap();
      const itemsToMove = this.currentIndex * this.itemsPerView;
      const scrollPosition = itemsToMove * (cardWidth + gap);
      
      
      if (this.viewport) {
        this.viewport.scrollTo({
          left: scrollPosition,
          behavior: animated ? 'smooth' : 'auto'
        });
      }
      
      this.updateNavButtons();
      this.updateDots();
      return;
    }
    
    const cardWidth = this.cards[0].offsetWidth;
    const gap = this.getGap();
    
    // Calculate position based on page index and items per view
    const itemsToMove = this.currentIndex * this.itemsPerView;
    const translateX = -(itemsToMove * (cardWidth + gap));
    
    this.container.style.transition = animated ? `transform ${this.sliderSpeed}ms ease` : 'none';
    this.container.style.transform = `translateX(${translateX}px)`;
    
    this.prevTranslate = translateX;
    this.currentTranslate = translateX;
    
    this.updateNavButtons();
    this.updateDots();
  }

  updateNavButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }
  }

  updateDots() {
    if (!this.dotsContainer) return;
    
    const dots = this.dotsContainer.querySelectorAll('.hdt-slider-dot');
    
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentIndex);
    });
    
  }

  startAutoplay() {
    if (!this.autoplay || window.innerWidth <= 768) return;
    
    this.autoplayInterval = setInterval(() => {
      if (this.currentIndex < this.maxIndex) {
        this.next();
      } else {
        this.currentIndex = 0;
        this.updateSliderPosition();
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.updateSliderSettings();
      this.createDots();
      this.updateSliderPosition(false);
      
      // Restart autoplay on desktop
      this.stopAutoplay();
      if (this.autoplay && window.innerWidth > 768) {
        this.startAutoplay();
      }
    }, 250);
  }

  destroy() {
    this.stopAutoplay();
    window.removeEventListener('resize', this.handleResize.bind(this));
  }
}

// Make class globally available
window.ProductSliderHorizontal = ProductSliderHorizontal;

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductSliderHorizontal;
}
